// FE-SCRM-011 — Opportunity detail page.
//
// Route: ``/dashboard/sales-crm/opportunities/:opportunityId``.
//
// v1 surface: header (name + type + stage), BOR panel (only when
// ``opportunity_type === "bor_takeover"``), and a Sales-Activity
// timeline scoped to this opportunity. Other tabs (stakeholders,
// activities form, files) will land in their own chunks — keeping
// this page narrow now means BE-SCRM-010's BOR flow is
// user-reachable without inheriting the full Opportunity edit
// surface scope.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { ActivityComposerModal } from "../activities/ActivityComposerModal";
import { QuickAddActivityMenu } from "../activities/QuickAddActivityMenu";
import { ACTIVITY_TYPE_META } from "../activities/activityTypeMeta";
import {
  useCreateActivityMutation,
  usePatchActivityMutation,
} from "../activities/hooks";
import type {
  ActivityCreateInput,
  ActivityPatchInput,
  ActivityType,
} from "../activities/types";
import { CoverageLinesPanel } from "../CoverageLines";
import { PlaybookCard } from "../Playbook";
import { ProposalsCard } from "../Proposals";

import { BorPanel } from "./BorPanel";
import { ConvertToAccountControl } from "./ConvertToAccountModal";
import {
  ACTIVITIES_QUERY_KEY,
  useOpportunityActivitiesQuery,
  useOpportunityDetailQuery,
} from "./hooks";
import type { OpportunityDetail, SalesActivity } from "./types";


// The OpportunityDetail module's SalesActivity types ``type`` as a
// bare ``string`` (wider than the activities module's ActivityType
// union — kept loose so the BE can land new taxons without forcing
// a FE rev). When we hand a row to the composer's edit mode, narrow
// here so the modal's strict ``ActivityType`` prop is satisfied;
// unknown taxons short-circuit to null and the caller skips rendering
// the edit modal.
const KNOWN_ACTIVITY_TYPES: ReadonlySet<string> = new Set(
  ACTIVITY_TYPE_META.map((m) => m.value),
);


function asActivityType(t: string): ActivityType | null {
  return KNOWN_ACTIVITY_TYPES.has(t) ? (t as ActivityType) : null;
}


const STAGE_LABEL: Record<string, string> = {
  discovery: "Discovery",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
};


const TYPE_LABEL: Record<string, string> = {
  new_business: "New business",
  renewal: "Renewal",
  bor_takeover: "BOR takeover",
};


const ACTIVITY_TYPE_LABEL: Record<string, string> = {
  bor_letter_generated: "BOR letter generated",
  meeting: "Meeting",
  call: "Call",
  email_in: "Email received",
  email_out: "Email sent",
  note: "Note",
  rfp_sent: "RFP sent",
  discovery_meeting: "Discovery meeting",
  decision_meeting: "Decision meeting",
};


function activityLabel(type: string): string {
  return ACTIVITY_TYPE_LABEL[type] ?? type.replace(/_/g, " ");
}


export function OpportunityDetailPage() {
  const { opportunityId } = useParams();
  const detail = useOpportunityDetailQuery(opportunityId);
  const activities = useOpportunityActivitiesQuery(opportunityId);

  if (!opportunityId) {
    return <NotFoundView />;
  }

  if (detail.isLoading) {
    return (
      <div
        data-testid="opportunity-detail-page"
        className="flex flex-col gap-5"
      >
        <DashboardLoadingState variant="card" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div
        data-testid="opportunity-detail-page"
        className="flex flex-col gap-5"
      >
        <DashboardErrorState
          title="Could not load opportunity"
          description={
            "We weren't able to fetch this opportunity. It may have been "
            + "removed, or you may not have access in the active entity."
          }
        />
      </div>
    );
  }

  const opp = detail.data;

  return (
    <div
      data-testid="opportunity-detail-page"
      className="flex flex-col gap-5"
    >
      <Header opportunity={opp} />

      {opp.opportunity_type === "bor_takeover" ? (
        <BorPanel opportunity={opp} />
      ) : null}

      <PlaybookCard
        opportunity={{
          opportunity_id: opp.opportunity_id,
          renewal_year: opp.renewal_year,
          renewal_month: opp.renewal_month,
          playbook_applied_id: opp.playbook_applied_id,
        }}
      />

      <CoverageLinesPanel opportunityId={opp.opportunity_id} />

      <ProposalsCard
        opportunity={{
          opportunity_id: opp.opportunity_id,
          prospect_id: opp.prospect_id,
        }}
      />

      <ActivityTimeline
        opportunityId={opp.opportunity_id}
        rows={activities.data?.rows ?? []}
        isLoading={activities.isLoading}
      />
    </div>
  );
}


function Header({ opportunity }: { opportunity: OpportunityDetail }) {
  return (
    <header className="flex flex-col gap-1.5">
      <Link
        to="/dashboard/sales-crm"
        className="text-xs text-text-tertiary hover:text-text-primary"
      >
        ← Back to Sales CRM
      </Link>
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          {opportunity.name}
        </h1>
        <ConvertToAccountControl opportunity={opportunity} />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span
          className={[
            "rounded-full bg-recessed px-2 py-0.5",
            "uppercase tracking-wide text-text-tertiary",
          ].join(" ")}
        >
          {TYPE_LABEL[opportunity.opportunity_type]
            ?? opportunity.opportunity_type}
        </span>
        <span className="text-text-secondary">
          {STAGE_LABEL[opportunity.stage] ?? opportunity.stage}
        </span>
      </div>
    </header>
  );
}


function ActivityTimeline({
  opportunityId, rows, isLoading,
}: {
  opportunityId: string;
  rows: SalesActivity[];
  isLoading: boolean;
}) {
  // ``composer_type`` doubles as both the modal's open state and the
  // ActivityComposer's initial type. ``null`` = closed; an
  // ``ActivityType`` = opened with that type pre-selected. The
  // QuickAddActivityMenu hands ``null`` for the "Custom…" row, which
  // we widen here to "follow_up" (the same default ActivityRail uses
  // in the bound-anchor case) so the composer opens with a sensible
  // type that the producer can change inside the modal.
  const [composer_type, setComposerType] = useState<ActivityType | null>(
    null,
  );
  const [editing_activity, setEditingActivity] = useState<SalesActivity | null>(
    null,
  );
  const qc = useQueryClient();
  const create_mutation = useCreateActivityMutation();
  const patch_mutation = usePatchActivityMutation();

  function invalidateTimeline() {
    // The activities/hooks mutation already invalidates the
    // ``sales-activities`` list cache, but the opportunity detail
    // page uses its own ``opportunity-activities`` query key
    // (OpportunityDetail/hooks.ts) — invalidate that too so the
    // freshly-mutated activity appears in the timeline without a
    // full page reload.
    qc.invalidateQueries({
      queryKey: [ACTIVITIES_QUERY_KEY, opportunityId],
    });
  }

  function handleSubmit(input: ActivityCreateInput) {
    create_mutation.mutate(input, {
      onSuccess: () => {
        invalidateTimeline();
        setComposerType(null);
      },
    });
  }

  function handlePatch(patch: ActivityPatchInput) {
    if (!editing_activity) return;
    patch_mutation.mutate(
      { activity_id: editing_activity.activity_id, patch },
      {
        onSuccess: () => {
          invalidateTimeline();
          setEditingActivity(null);
        },
      },
    );
  }

  return (
    <section
      data-testid="opportunity-activity-timeline"
      className="rounded-md border border-border-subtle bg-raised p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-text-primary">
          Activity
        </h2>
        <QuickAddActivityMenu
          on_pick={(t) => setComposerType(t ?? "follow_up")}
        />
      </div>
      {isLoading ? (
        <div className="mt-2">
          <DashboardLoadingState variant="card" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-xs text-text-tertiary">
          No activity yet. Calls, meetings, and BOR letters land here
          as they happen.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1 text-xs">
          {rows.map((a) => (
            <li
              key={a.activity_id}
              data-testid={`activity-row-${a.activity_id}`}
            >
              <button
                type="button"
                data-testid={`activity-row-edit-${a.activity_id}`}
                onClick={() => setEditingActivity(a)}
                className={[
                  "flex w-full items-baseline justify-between gap-3",
                  "rounded-md px-2 py-1 text-left",
                  "hover:bg-recessed focus-visible:bg-recessed",
                  "focus-visible:outline-none",
                ].join(" ")}
              >
                <div className="flex flex-col">
                  <span className="font-medium text-text-primary">
                    {activityLabel(a.type)}
                  </span>
                  {a.subject ? (
                    <span className="text-text-secondary">{a.subject}</span>
                  ) : null}
                </div>
                {a.completed_at ? (
                  <time className="text-text-tertiary">
                    {a.completed_at.slice(0, 10)}
                  </time>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
      {composer_type !== null && (
        <ActivityComposerModal
          initial_type={composer_type}
          bound_opportunity_id={opportunityId}
          is_submitting={create_mutation.isPending}
          on_submit={handleSubmit}
          on_cancel={() => setComposerType(null)}
        />
      )}
      {editing_activity !== null
        && asActivityType(editing_activity.type) !== null && (
        <ActivityComposerModal
          initial_type={asActivityType(editing_activity.type)!}
          bound_opportunity_id={opportunityId}
          editing={{
            initial_subject: editing_activity.subject ?? "",
            initial_body: editing_activity.body,
            initial_due_at: editing_activity.due_at,
          }}
          is_submitting={patch_mutation.isPending}
          on_submit={() => {
            // Unused in edit mode — the composer dispatches
            // ``on_submit_patch`` instead when ``editing`` is set.
          }}
          on_submit_patch={handlePatch}
          on_cancel={() => setEditingActivity(null)}
        />
      )}
    </section>
  );
}


function NotFoundView() {
  return (
    <div
      data-testid="opportunity-detail-page"
      className="flex flex-col gap-5"
    >
      <DashboardEmptyState
        title="Opportunity not found"
        description={
          "The opportunity you're looking for doesn't exist or isn't "
          + "accessible in this entity."
        }
      />
    </div>
  );
}
